import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import ExportButton from "@/components/ExportButton";

const money = (v) => `$${(Number(v) || 0).toLocaleString("es-EC", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function ReactivacionDialog({ onClose }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [sending, setSending] = useState(false);
  const verTodos = ["admin", "gerencia", "contabilidad"].includes(user?.role);
  useEffect(() => {
    api.get("/reactivacion/clientes").then(r => setRows(r.data)).catch(e => { toast.error(formatApiError(e)); setRows([]); });
  }, []);

  const enviar = async () => {
    if (!window.confirm("Se enviará ahora el aviso a cada vendedor (campana + correo) y el resumen de ventas a ventas@pharma-stern.com. ¿Continuar?")) return;
    setSending(true);
    try {
      const { data } = await api.post("/reactivacion/enviar-ahora");
      toast.success(`Enviado a ${data.vendedores.length} vendedor(es). Resumen a ${data.resumen_gerencia_destino}: ${data.resumen_gerencia_ok === true ? "OK" : data.resumen_gerencia_ok}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const cols = [{ label: "Cliente", accessor: "cliente" }, { label: "Vendedor", accessor: "vendedor" }, { label: "Última compra", accessor: "ultima_compra" },
    { label: "Días sin comprar", accessor: "dias_sin_comprar" }, { label: "Ciclo (días)", accessor: "frecuencia_dias" }, { label: "Compras 12m", accessor: "monto_12m" }, { label: "Teléfono", accessor: "telefono" }];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-auto" data-testid="reactivacion-dialog">
        <DialogHeader><DialogTitle>Clientes por reactivar</DialogTitle></DialogHeader>
        <p className="text-xs text-[#4a5c53]">Llevan más de 1,5 veces su ciclo habitual sin comprar (mínimo 3 compras). Cada lunes llega este aviso a cada vendedor por la campana y por correo.</p>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold" data-testid="reactivacion-count">{rows ? `${rows.length} cliente(s)` : ""}</span>
          <div className="flex-1" />
          {rows && <ExportButton title="Clientes por reactivar" columns={cols} rows={rows} filename="clientes-por-reactivar" />}
          {["admin", "gerencia"].includes(user?.role) && (
            <Button size="sm" variant="outline" onClick={enviar} disabled={sending} data-testid="reactivacion-enviar-ahora">
              {sending ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Send size={13} className="mr-1" />}Enviar avisos ahora
            </Button>
          )}
        </div>
        {!rows ? <Loader2 className="animate-spin" /> : (
          <table className="erp-table">
            <thead><tr><th>Cliente</th>{verTodos && <th>Vendedor</th>}<th>Última compra</th><th className="text-right">Sin comprar</th><th className="text-right">Ciclo</th><th className="text-right">Compras 12 m</th><th></th></tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.cliente_id} data-testid={`reactivar-row-${r.cliente_id}`}>
                  <td className="font-medium text-sm">{r.cliente}<div className="text-[10px] text-[#8ba397]">{r.ciudad || ""} {r.telefono ? `· ${r.telefono}` : ""}</div></td>
                  {verTodos && <td className="text-xs">{r.vendedor || "Sin asignar"}</td>}
                  <td className="text-xs">{r.ultima_compra}</td>
                  <td className="text-right tabular-nums text-[#b53a25] font-semibold">{r.dias_sin_comprar} d</td>
                  <td className="text-right tabular-nums text-xs">cada {r.frecuencia_dias} d</td>
                  <td className="text-right tabular-nums">{money(r.monto_12m)}</td>
                  <td><Button size="sm" variant="ghost" className="text-[#205c45]" onClick={() => navigate(`/clientes/${r.cliente_id}/perfil`)} data-testid={`reactivar-perfil-${r.cliente_id}`}>Perfil</Button></td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-xs text-[#8ba397]">Ningún cliente atrasado. ¡Bien!</td></tr>}
            </tbody>
          </table>
        )}
      </DialogContent>
    </Dialog>
  );
}
